<template>
  <div class="app-wrapper">
    <header class="top-header">
      <div class="header-brand">
        <div class="brand-mark">T</div>
        <div class="brand-info">
          <span class="brand-name">thxily</span>
          <span class="brand-sub">管理コンソール</span>
        </div>
      </div>
      <button class="back-btn" v-on:click="$router.push('/menu')">← メニューへ戻る</button>
    </header>
    <main class="main-content">
      <div class="white-card" :class="{ processing: isProcessing }">

        <!-- 決済完了画面 -->
        <div v-if="currentView === 'completed'">
          <h2 class="page-title">決済完了</h2>
          <div class="completion-message">
            <div class="completion-details">
              <div class="detail-row">
                <span class="detail-label">決済金額</span>
                <span class="detail-value">¥{{ formatAmount(paymentData.amount) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">注文番号</span>
                <span class="detail-value">{{ paymentData.orderNumber }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">決済日時</span>
                <span class="detail-value">{{ formatDate(new Date()) }}</span>
              </div>
            </div>
            <div class="btn-row">
              <button class="btn-primary" @click="goToMenu">
                メニューに戻る
              </button>
            </div>
          </div>
        </div>

        <!-- 決済確認画面 -->
        <div v-else-if="currentView === 'payment'">
          <h2 class="page-title">決済確認</h2>
          <div class="amount-display" :class="{ pulse: isProcessing }">
            <div class="amount-label">お支払い金額</div>
            <div class="amount-value">¥{{ formatAmount(paymentData.amount) }}</div>
            <div class="amount-currency">{{ paymentData.currency }}</div>
          </div>

          <div class="payment-details">
            <div class="detail-row">
              <span class="detail-label">商品名</span>
              <span class="detail-value">{{ paymentData.itemName }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">注文番号</span>
              <span class="detail-value">{{ paymentData.orderNumber }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">支払い方法</span>
              <div class="payment-method">
                <div class="card-badge">VISA</div>
                <span class="detail-value">**** **** **** {{ paymentData.cardLast4 }}</span>
              </div>
            </div>
            <div class="detail-row">
              <span class="detail-label">ステータス</span>
              <span class="status-badge" :class="statusClass">{{ statusText }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">取引日時</span>
              <span class="detail-value">{{ formatDate(paymentData.timestamp) }}</span>
            </div>
          </div>

          <div class="btn-row">
            <button class="btn-secondary" @click="cancel" :disabled="isProcessing">
              キャンセル
            </button>
            <button class="btn-primary" @click="processPayment" :disabled="isProcessing">
              <span v-if="isProcessing" class="loading-spinner"></span>
              {{ isProcessing ? '処理中...' : '決済実行' }}
            </button>
          </div>
        </div>

        <!-- クレジット登録画面 -->
        <div v-else-if="currentView === 'credit'">
          <h2 class="page-title">クレジットカード登録</h2>
          <form @submit.prevent="registerCard">
            <div class="form-group">
              <label class="form-label">カード番号</label>
              <input type="text" v-model="cardData.number" @input="formatCardNumber" placeholder="1234 5678 9012 3456" maxlength="19" class="form-control"/>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">有効期限</label>
                <input type="text" v-model="cardData.expiry" @input="formatExpiry" placeholder="MM/YY" maxlength="5" class="form-control"/>
              </div>
              <div class="form-group">
                <label class="form-label">CVV</label>
                <input type="text" v-model="cardData.cvv" @input="formatCVV" placeholder="123" maxlength="3" class="form-control"/>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">カード名義人</label>
              <input type="text" v-model="cardData.name" placeholder="YAMADA TARO" class="form-control"/>
            </div>
            <div class="btn-row">
              <button type="button" class="btn-secondary" @click="goBackToPayment">
                戻る
              </button>
              <button type="submit" class="btn-primary" :disabled="isProcessing">
                <span v-if="isProcessing" class="loading-spinner"></span>
                {{ isProcessing ? '登録中...' : '決済確定' }}
              </button>
            </div>
          </form>
        </div>

      </div>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import router from "@/router";
import store from "@/store";

export default {
  mounted() {
    // URLハッシュをチェックして画面表示を決定
    this.checkUrlHash();

    // fincodeスクリプトを動的に読み込み
    if (!window.Fincode) {
      const script = document.createElement('script');
      script.src = 'https://js.test.fincode.jp/v1/fincode.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // ハッシュ変更を監視
    window.addEventListener('hashchange', this.checkUrlHash);
  },
  beforeUnmount() {
    // ハッシュ変更の監視を削除
    window.removeEventListener('hashchange', this.checkUrlHash);
  },
  name: 'PaymentDisplay',
  data() {
    return {
      currentView: 'payment', // 'payment' or 'credit'
      isProcessing: false,
      paymentData: {
        amount: 100,
        currency: 'Japanese Yen',
        itemName: 'プレミアムプラン (1年)',
        orderNumber: 'ORD-2025-001234',
        cardLast4: '4567',
        timestamp: new Date(),
        pay_type: 'Card',
        job_code: 'CAPTURE'
      },
      transaction: {
        id: "",
        access_id: '',
        shop_id : '',
        card_no: '',
        expire: '',
        method: '1',
        token: '',
        // tds2_ret_url: 'https://your-domain.com/payment-callback'  // 重要
      },
      cardData: {
        number: '',
        expiry: '',
        cvv: '',
        name: ''
      },
      return_url: ''
    };
  },
  computed: {
    statusClass() {
      return this.isProcessing ? 'status-pending' : 'status-approved';
    },
    statusText() {
      return this.isProcessing ? '処理中' : '承認済み';
    }
  },
  methods: {
    formatAmount(amount) {
      return amount.toLocaleString('ja-JP');
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}/${month}/${day} ${hours}:${minutes}`;
    },
    async processPayment() {
      this.isProcessing = true;

      try {
        // await this.simulatePaymentProcess();
        // alert('✅ 決済が正常に完了しました！\n\n領収書をメールで送信いたします。');

        axios.post("/payments",{"amount":"100"}).then((response) => {
          console.log( "response.data", response.data)
          console.log( "response.data.data", response.data.data.access_id)
          this.transaction.access_id = response.data.data.access_id
          this.transaction.id = response.data.data.id
          this.transaction.shop_id = response.data.data.shop_id?.id || response.data.data.shop_id
          this.transaction.amount = this.paymentData.amount
          this.currentView = 'credit'; // クレジット登録画面に切り替え
        });
      } catch (error) {
        console.log(error)
        alert('❌ 決済処理中にエラーが発生しました。\n\nもう一度お試しください。');
      } finally {
        this.isProcessing = false;
      }
    },
    simulatePaymentProcess() {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 3000);
      });
    },
    async cancel() {
      if (confirm('決済をキャンセルしますか？')) {
        alert('決済がキャンセルされました。');

        //顧客登録
        await axios.post("/register_customer").then((response) => {
        });

        // // カード登録
        // const card = {
        //   customer_id: 'c_G8Wj55UmSFyeEB66pKvvOA',
        //   default_flag: '1',     // デフォルトフラグ
        //   card_no: '5111111111111111',
        //   expire: '2907',                // カード有効期限(yymm)
        //   holder_name: 'YAMADA TARO',       // カード名義人
        //   security_code: '123',   // セキュリティコード
        //   type: '1',
        // }
        // let fincode = window.Fincode('p_test_MjFlMzI2YjUtNTM3Zi00NWQ2LWFjMjUtY2I2NWE0MzQyZmE5YjIwNWFiYmQtMGM1NC00OGI2LWI1NDQtM2E0NjlkYzY3YzRic18yNTA2MDU0NDk4MQ');
        // fincode.cards(card,
        //     function (status, response) {
        //         if (status === 200) {
        //             console.log(response);
        //             // リクエスト正常時の処理
        //         } else {
        //             // リクエストエラー時の処理
        //             console.log("エラー");
        //             console.log(response);
        //         }
        //     },
        //     function () {
        //         // 通信エラー処理。
        //     }
        // )

        // 決済登録（テスト用）
        try {
          await axios.post("/payments2", {"amount":"200"});
          console.log("payments2 成功");
        } catch (error) {
          console.error("payments2 エラー:", error);
        }

        this.resetForm();
      }
    },
    resetForm() {
      this.paymentData.timestamp = new Date();
    },
    goBackToPayment() {
      this.currentView = 'payment';
    },
    async registerCard() {
      this.isProcessing = true;

      try {
          // fincodeライブラリが読み込まれるまで待機
          if (!window.Fincode) {
            alert('決済システムの読み込み中です。少し待ってからもう一度お試しください。');
            return;
          }

          let fincode = window.Fincode('p_test_MjFlMzI2YjUtNTM3Zi00NWQ2LWFjMjUtY2I2NWE0MzQyZmE5YjIwNWFiYmQtMGM1NC00OGI2LWI1NDQtM2E0NjlkYzY3YzRic18yNTA2MDU0NDk4MQ');

          // フォームデータをtransactionオブジェクトにマッピング
          const transaction = {
            id: this.transaction.id,
            access_id: this.transaction.access_id,
            amount: this.paymentData.amount,
            pay_type: 'Card',
            job_code: 'CAPTURE',
            card_no: this.cardData.number.replace(/\s/g, ''), // スペースを除去
            expire: this.cardData.expiry.includes('/') ?
              this.cardData.expiry.split('/')[1] + this.cardData.expiry.split('/')[0] :
              this.cardData.expiry, // MM/YY -> YYMM
            security_code: this.cardData.cvv,
            holder_name: this.cardData.name,
            method: '1', // 一括払い
                            return_url: 'http://localhost:8000/payment-callback'
          };

          console.log( "送信するtransaction:", transaction);

          try {
            fincode.payments(transaction, function (status, response) {
                console.log('fincode response status:', status);
                console.log('fincode response:', response);

                if (status === 200) {
                    if (response.redirect_url) {
                        // 3Dセキュア認証が必要 - ACS URLにリダイレクト
                        window.location.href = response.redirect_url;
                    } else {
                        // 3Dセキュア認証不要 - 直接完了処理
                        alert('✅ 決済が正常に完了しました！');
                        // window.location.href = '/payment-success';
                    }
                } else {
                    // エラー処理
                    console.error('決済エラー:', response);
                    alert('❌ 決済エラーが発生しました: ' + (response.errors ? response.errors[0].error_message : '不明なエラー'));
                }
            });
          } catch (error) {
            console.log(error)
            alert('❌ 決済処理中にエラーが発生しました。\n\nもう一度お試しください。');
          } finally {
            this.isProcessing = false;
          }
      } catch (error) {
        console.log(error);
        alert('❌ カード登録中にエラーが発生しました。\n\nもう一度お試しください。');
      } finally {
        this.isProcessing = false;
      }
    },
    // カード番号のフォーマット（4桁ごとにスペース）
    formatCardNumber() {
      let value = this.cardData.number.replace(/\s/g, '').replace(/[^0-9]/gi, '');
      let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value;
      this.cardData.number = formattedValue;
    },
    // 有効期限のフォーマット（MM/YY）
    formatExpiry() {
      let value = this.cardData.expiry.replace(/\D/g, '');
      if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2, 4);
      }
      this.cardData.expiry = value;
    },
    // CVVは数字のみ
    formatCVV() {
      this.cardData.cvv = this.cardData.cvv.replace(/[^0-9]/g, '');
           },
     checkUrlHash() {
       // URLハッシュに #fin が含まれているかチェック
       if (window.location.hash.includes('fin')) {
         this.currentView = 'completed';
       } else {
         this.currentView = 'payment';
       }
     },
     goToMenu() {
       // メニュー画面に遷移
       this.$router.push('/menu');
     }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap');

.app-wrapper {
  min-height: 100vh;
  background: #DDE2EC;
  font-family: 'Noto Sans JP', 'Segoe UI', sans-serif;
}

.top-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  background: #1E2A3A;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.header-brand { display: flex; align-items: center; gap: 12px; }

.brand-mark {
  width: 38px; height: 38px; border-radius: 10px;
  background: linear-gradient(135deg, #4F8EF7, #7C5CFC);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; font-weight: 800; color: #fff;
}

.brand-info { display: flex; flex-direction: column; line-height: 1.2; }
.brand-name { font-size: 1rem; font-weight: 700; color: #fff; }
.brand-sub  { font-size: 0.68rem; color: #8A9BB0; }

.back-btn {
  padding: 8px 20px; border-radius: 8px;
  border: 1.5px solid #94A3B8; background: transparent;
  color: #94A3B8; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; font-family: inherit;
  transition: background 0.2s, color 0.2s;
}
.back-btn:hover { background: #94A3B8; color: #1E2A3A; }

.main-content {
  max-width: 960px; margin: 0 auto;
  padding: 40px 24px 80px;
}

.white-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.10);
  padding: 32px 40px;
}

.page-title {
  font-size: 1.3rem; font-weight: 700;
  color: #1E293B; margin: 0 0 24px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #E2E8F0;
}

/* フォーム */
.form-group { margin-bottom: 20px; }

.form-label {
  display: block; font-size: 0.85rem; font-weight: 600;
  color: #475569; margin-bottom: 6px;
}

.form-control {
  width: 100%; padding: 10px 14px; border-radius: 8px;
  border: 1.5px solid #CBD5E1; font-size: 0.9rem;
  font-family: inherit; outline: none; box-sizing: border-box;
  transition: border-color 0.2s;
}
.form-control:focus { border-color: #3B82F6; }

.form-row {
  display: flex;
  gap: 16px;
}
.form-row .form-group { flex: 1; }

/* ボタン */
.btn-row {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.btn-primary {
  padding: 10px 24px; border-radius: 8px; border: none;
  background: #3B82F6; color: #fff;
  font-size: 0.9rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: background 0.2s;
  display: inline-flex; align-items: center; gap: 8px;
}
.btn-primary:hover:not(:disabled) { background: #2563EB; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  padding: 10px 24px; border-radius: 8px;
  border: 1.5px solid #CBD5E1; background: #fff;
  color: #475569; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: background 0.2s;
}
.btn-secondary:hover:not(:disabled) { background: #F1F5F9; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

/* 金額表示 */
.amount-display {
  text-align: center;
  background: #F8FAFC;
  border-radius: 12px;
  padding: 32px 24px;
  margin-bottom: 24px;
  border: 1.5px solid #E2E8F0;
}

.amount-label {
  color: #64748B;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.amount-value {
  font-size: 2.8rem;
  font-weight: 700;
  color: #3B82F6;
  margin-bottom: 6px;
  font-family: monospace;
}

.amount-currency {
  color: #64748B;
  font-size: 0.9rem;
}

/* 決済詳細 */
.payment-details { margin-bottom: 8px; }

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #F1F5F9;
  gap: 12px;
}
.detail-row:last-child { border-bottom: none; }

.detail-label {
  color: #64748B;
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 100px;
}

.detail-value {
  color: #1E293B;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  flex: 1;
}

.card-badge {
  width: 36px; height: 22px;
  background: #3B82F6;
  border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 0.65rem; font-weight: 700;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-pending {
  background: #FEF3C7;
  color: #92400E;
}

.status-approved {
  background: #D1FAE5;
  color: #065F46;
}

/* 完了画面 */
.completion-message { text-align: left; }

.completion-details {
  background: #F8FAFC;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
}

/* ローディングスピナー */
.loading-spinner {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.processing { opacity: 0.85; }

.pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.01); }
}
</style>
